import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, Users, 
  LogOut, Menu, Plus, Edit2, Trash2, X, Upload, Image, RotateCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProducts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    description: '',
    category: 'men',
    image_url: '',
    price: '',
    original_price: '',
    fragrance_family: '',
    top_notes: '',
    heart_notes: '',
    base_notes: '',
    stock: 0,
    sizes: [{ ml: 50, price: 0, stock: 10 }]
  });
  
  const adminToken = localStorage.getItem('adminToken');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    loadProducts();
  }, [adminToken]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Afbeelding mag maximaal 5MB zijn');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Alleen afbeeldingen zijn toegestaan');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Set the uploaded image URL
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Afbeelding geüpload!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Afbeelding uploaden mislukt');
    } finally {
      setUploading(false);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products?limit=100`);
      setProducts(response.data.products);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        stock: parseInt(formData.stock),
        top_notes: formData.top_notes.split(',').map(s => s.trim()).filter(Boolean),
        heart_notes: formData.heart_notes.split(',').map(s => s.trim()).filter(Boolean),
        base_notes: formData.base_notes.split(',').map(s => s.trim()).filter(Boolean),
        sizes: formData.sizes.map(s => ({
          ml: parseInt(s.ml),
          price: parseFloat(s.price),
          stock: parseInt(s.stock)
        }))
      };

      if (editProduct) {
        // Support both id and _id for backwards compatibility
        const productId = editProduct.id || editProduct._id;
        if (!productId) {
          toast.error('Product ID ontbreekt - herlaad de pagina');
          return;
        }
        await axios.put(`${API}/admin/products/${productId}`, data, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/admin/products`, data, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        toast.success('Product created');
      }
      
      setShowModal(false);
      resetForm();
      loadProducts();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    const productId = product.id || product._id;
    if (!productId) {
      toast.error('Product ID ontbreekt');
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Product deleted');
      loadProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleGenerate360 = async (product) => {
    if (!window.confirm('Dit genereert 4 AI-afbeeldingen voor 360° weergave. Dit kan enkele minuten duren. Doorgaan?')) return;
    
    const productId = product.id || product._id;
    if (!productId) {
      toast.error('Product ID ontbreekt');
      return;
    }
    
    toast.loading('360° views genereren...', { id: '360-gen' });
    
    try {
      const response = await axios.post(`${API}/ai/generate-360-views`, 
        { product_id: productId },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      toast.success(`${response.data.views?.length || 0} views gegenereerd!`, { id: '360-gen' });
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Genereren mislukt', { id: '360-gen' });
    }
  };

  const handleEdit = (product) => {
    // Store the product with its ID for later use
    setEditProduct({
      ...product,
      // Ensure id is available
      id: product.id || product._id
    });
    setFormData({
      name: product.name,
      brand: product.brand,
      description: product.description,
      category: product.category,
      image_url: product.image_url,
      price: product.price.toString(),
      original_price: product.original_price?.toString() || '',
      fragrance_family: product.fragrance_family,
      top_notes: product.top_notes?.join(', ') || '',
      heart_notes: product.heart_notes?.join(', ') || '',
      base_notes: product.base_notes?.join(', ') || '',
      stock: product.stock,
      sizes: product.sizes || [{ ml: 50, price: 0, stock: 10 }]
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditProduct(null);
    setFormData({
      name: '',
      brand: '',
      description: '',
      category: 'men',
      image_url: '',
      price: '',
      original_price: '',
      fragrance_family: '',
      top_notes: '',
      heart_notes: '',
      base_notes: '',
      stock: 0,
      sizes: [{ ml: 50, price: 0, stock: 10 }]
    });
  };

  const addSize = () => {
    setFormData({
      ...formData,
      sizes: [...formData.sizes, { ml: 0, price: 0, stock: 0 }]
    });
  };

  const removeSize = (index) => {
    setFormData({
      ...formData,
      sizes: formData.sizes.filter((_, i) => i !== index)
    });
  };

  const updateSize = (index, field, value) => {
    const newSizes = [...formData.sizes];
    newSizes[index][field] = value;
    setFormData({ ...formData, sizes: newSizes });
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/orders', label: 'Orders', icon: ShoppingCart },
    { path: '/admin/products', label: 'Products', icon: Package },
    { path: '/admin/customers', label: 'Customers', icon: Users },
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 glass-strong transform transition-transform duration-300
        lg:translate-x-0 lg:static lg:inset-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-[#2C1B18]">
            <h1 className="font-heading text-xl">
              <span className="gold-text">Zuiver</span>
              <span className="text-white">Parfum</span>
            </h1>
            <p className="text-[#57534E] text-xs mt-1">Admin Portal</p>
          </div>
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === item.path
                    ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                    : 'text-[#A8A29E] hover:text-white hover:bg-white/5'
                }`}
              >
                <item.icon size={20} />
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-[#2C1B18]">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-500 hover:bg-red-500/10 w-full transition-colors"
            >
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        <header className="glass-strong sticky top-0 z-30 px-4 md:px-6 py-4 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-white">
            <Menu size={24} />
          </button>
          <h2 className="font-heading text-xl text-white">Products</h2>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={18} />
            Add Product
          </button>
        </header>

        <div className="p-4 md:p-6">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {products.map((product) => (
                <div key={product.id || product._id} className="glass rounded-xl overflow-hidden">
                  <div className="aspect-square bg-[#121212] relative">
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2 flex gap-1">
                      <button
                        onClick={() => handleGenerate360(product)}
                        className={`p-2 rounded-lg transition-colors ${
                          product.has_360_view 
                            ? 'bg-green-500 hover:bg-green-600' 
                            : 'bg-[#57534E] hover:bg-[#D4AF37]'
                        }`}
                        title={product.has_360_view ? '360° beschikbaar' : '360° genereren'}
                      >
                        <RotateCw size={14} className="text-white" />
                      </button>
                      <button
                        onClick={() => handleEdit(product)}
                        className="p-2 bg-[#D4AF37] rounded-lg hover:bg-[#B45309] transition-colors"
                      >
                        <Edit2 size={14} className="text-black" />
                      </button>
                      <button
                        onClick={() => handleDelete(product)}
                        className="p-2 bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                      >
                        <Trash2 size={14} className="text-white" />
                      </button>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-[#D4AF37] text-xs uppercase tracking-wider">
                      {product.brand}
                    </p>
                    <h3 className="text-white font-heading">{product.name}</h3>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-[#D4AF37] font-bold">€{product.price.toFixed(2)}</p>
                      <p className="text-[#57534E] text-sm">Stock: {product.stock}</p>
                    </div>
                    <p className="text-[#57534E] text-xs mt-1">{product.category}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Product Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="glass rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-heading text-xl text-white">
                  {editProduct ? 'Edit Product' : 'Add New Product'}
                </h3>
                <button onClick={() => { setShowModal(false); resetForm(); }} className="text-[#A8A29E] hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Product Name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="input-luxury"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Brand"
                    value={formData.brand}
                    onChange={(e) => setFormData({...formData, brand: e.target.value})}
                    className="input-luxury"
                    required
                  />
                </div>

                <textarea
                  placeholder="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="input-luxury min-h-[100px]"
                  required
                />

                <div className="grid md:grid-cols-2 gap-4">
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="bg-transparent border-b border-[#57534E] py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                  >
                    <option value="men" className="bg-[#1C1917]">Men</option>
                    <option value="women" className="bg-[#1C1917]">Women</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Fragrance Family"
                    value={formData.fragrance_family}
                    onChange={(e) => setFormData({...formData, fragrance_family: e.target.value})}
                    className="input-luxury"
                    required
                  />
                </div>

                <input
                  type="url"
                  placeholder="Image URL"
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  className="input-luxury"
                  required
                />

                {/* Image Upload Section */}
                <div className="border-2 border-dashed border-[#2C1B18] rounded-xl p-4 text-center hover:border-[#D4AF37] transition-colors">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  
                  {formData.image_url ? (
                    <div className="relative inline-block">
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        className="max-h-32 rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, image_url: ''})}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex flex-col items-center gap-2 mx-auto py-4 text-[#A8A29E] hover:text-[#D4AF37]"
                    >
                      {uploading ? (
                        <>
                          <div className="spinner"></div>
                          <span className="text-sm">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Upload size={24} />
                          <span className="text-sm">Upload afbeelding of plak URL hierboven</span>
                        </>
                      )}
                    </button>
                  )}
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <input
                    type="number"
                    placeholder="Price"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className="input-luxury"
                    step="0.01"
                    required
                  />
                  <input
                    type="number"
                    placeholder="Original Price (optional)"
                    value={formData.original_price}
                    onChange={(e) => setFormData({...formData, original_price: e.target.value})}
                    className="input-luxury"
                    step="0.01"
                  />
                  <input
                    type="number"
                    placeholder="Total Stock"
                    value={formData.stock}
                    onChange={(e) => setFormData({...formData, stock: e.target.value})}
                    className="input-luxury"
                  />
                </div>

                {/* Sizes */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[#A8A29E] text-sm">Sizes</label>
                    <button type="button" onClick={addSize} className="text-[#D4AF37] text-sm hover:underline">
                      + Add Size
                    </button>
                  </div>
                  {formData.sizes.map((size, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="number"
                        placeholder="ML"
                        value={size.ml}
                        onChange={(e) => updateSize(index, 'ml', e.target.value)}
                        className="input-luxury flex-1"
                      />
                      <input
                        type="number"
                        placeholder="Price"
                        value={size.price}
                        onChange={(e) => updateSize(index, 'price', e.target.value)}
                        className="input-luxury flex-1"
                        step="0.01"
                      />
                      <input
                        type="number"
                        placeholder="Stock"
                        value={size.stock}
                        onChange={(e) => updateSize(index, 'stock', e.target.value)}
                        className="input-luxury flex-1"
                      />
                      {formData.sizes.length > 1 && (
                        <button type="button" onClick={() => removeSize(index)} className="text-red-500">
                          <X size={18} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Fragrance Notes */}
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Top Notes (comma separated)"
                    value={formData.top_notes}
                    onChange={(e) => setFormData({...formData, top_notes: e.target.value})}
                    className="input-luxury"
                  />
                  <input
                    type="text"
                    placeholder="Heart Notes (comma separated)"
                    value={formData.heart_notes}
                    onChange={(e) => setFormData({...formData, heart_notes: e.target.value})}
                    className="input-luxury"
                  />
                  <input
                    type="text"
                    placeholder="Base Notes (comma separated)"
                    value={formData.base_notes}
                    onChange={(e) => setFormData({...formData, base_notes: e.target.value})}
                    className="input-luxury"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button type="submit" className="btn-primary flex-1">
                    {editProduct ? 'Update Product' : 'Create Product'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminProducts;
